import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import OwnerBadge from '../Common/OwnerBadge';
import SearchableSelect from '../Common/SearchableSelect';
import { apiService } from '../../services/api';

const formatEtpDisplay = (etp) => {
  if (!etp) return '';
  const name = String(etp.name || '').trim();
  const operator = String(etp.operator || '').trim();
  if (!name) return operator;
  return operator ? `${name} (${operator})` : name;
};

const formatEtpSelected = (etp) => {
  if (!etp) return '';
  const operator = String(etp.operator || '').trim();
  const name = String(etp.name || '').trim();
  return operator || name;
};

const AccountForm = ({ 
  account, 
  formData, 
  setFormData, 
  onSave, 
  onCancel, 
  isViewMode = false,
  onFormChange,
  debtors = [],
  organizers = [],
  etps = [],
  onCreateOwner,
  onEditOwner,
  lockPurpose = false,
  lockOwnerType = false,
  lockOwnerId = false,
  forcedOwnerIdsByType = {},
  onOwnerSelected
}) => {
  const [initialFormData, setInitialFormData] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [bankSuggestions, setBankSuggestions] = useState([]);
  const [bankSuggestionsOpen, setBankSuggestionsOpen] = useState(false);
  const suppressNextBankSearchRef = useRef(false);

  // Инициализация формы
  useEffect(() => {
    let newFormData;
    
    if (account) {
      newFormData = { 
        ...account,
        owner_type: account.owner_type || 'platform',
        purpose: account.purpose || 'deposit'
      };
    } else {
      newFormData = {
        account_number: '',
        bank_name: '',
        bik: '',
        corr_account: '',
        owner_type: 'platform',
        owner_id: '',
        purpose: 'deposit'
      };
    }
    
    setFormData(newFormData);
    setInitialFormData(JSON.parse(JSON.stringify(newFormData)));
    setValidationErrors({});
    if (onFormChange) {
      onFormChange(false);
    }
  }, [account, setFormData]);

  // Отслеживание изменений в форме
  useEffect(() => {
    if (initialFormData) {
      const changed = JSON.stringify(formData) !== JSON.stringify(initialFormData);
      if (onFormChange) {
        onFormChange(changed);
      }
    }
  }, [formData, initialFormData, onFormChange]);

  const sanitizeDigits = (value, maxLength) => String(value ?? '').replace(/\D/g, '').slice(0, maxLength);

  const updateFormData = (field, value) => {
    if ((field === 'purpose' && lockPurpose) || (field === 'owner_type' && lockOwnerType) || (field === 'owner_id' && lockOwnerId)) {
      return;
    }
    let nextValue = value;
    switch (field) {
      case 'account_number':
      case 'corr_account':
        nextValue = sanitizeDigits(value, 20);
        break;
      case 'bik':
        nextValue = sanitizeDigits(value, 9);
        break;
      case 'owner_id':
        if (value === '' || value === null || value === undefined) {
          nextValue = '';
        } else {
          const num = Number(value);
          nextValue = Number.isNaN(num) ? sanitizeDigits(value, 20) : num;
        }
        break;
      case 'owner_type':
        nextValue = value;
        break;
      default:
        break;
    }
    const updated = formData ? { ...formData } : {};
    updated[field] = nextValue;
    if (field === 'owner_type') {
      const nextOwnerType = String(nextValue || '').trim().toLowerCase();
      const nextForcedOwnerId = forcedOwnerIdsByType?.[nextOwnerType] ?? '';
      updated.owner_id = nextForcedOwnerId || '';
    }
    if (field === 'owner_id' && typeof onOwnerSelected === 'function' && formData?.owner_type) {
      onOwnerSelected(formData.owner_type, nextValue);
    }
    setFormData(updated);
  };

  const applyBankData = useCallback((item) => {
    if (!item) return;
    suppressNextBankSearchRef.current = true;
    setFormData((prev) => ({
      ...(prev || {}),
      bank_name: item.bank_name || prev?.bank_name || '',
      bik: sanitizeDigits(item.bik || prev?.bik || '', 9),
      corr_account: sanitizeDigits(item.corr_account || prev?.corr_account || '', 20),
    }));
    setBankSuggestions([]);
    setBankSuggestionsOpen(false);
  }, [setFormData]);

  useEffect(() => {
    if (isViewMode) return undefined;
    const bik = sanitizeDigits(formData?.bik || '', 9);
    if (bik.length !== 9) return undefined;
    const timer = setTimeout(async () => {
      try {
        const resp = await apiService.lookupBankByBik(bik);
        const rows = Array.isArray(resp?.data?.results) ? resp.data.results : [];
        if (rows[0]) {
          applyBankData(rows[0]);
        }
      } catch (_) {
        // молча: БИК мог быть введен вручную без DaData
      }
    }, 350);
    return () => clearTimeout(timer);
  }, [formData?.bik, isViewMode, applyBankData]);

  useEffect(() => {
    if (isViewMode) return undefined;
    if (suppressNextBankSearchRef.current) {
      suppressNextBankSearchRef.current = false;
      return undefined;
    }
    const query = String(formData?.bank_name || '').trim();
    if (query.length < 2) {
      setBankSuggestions([]);
      setBankSuggestionsOpen(false);
      return undefined;
    }
    const timer = setTimeout(async () => {
      try {
        const resp = await apiService.searchBanksByName(query);
        const rows = Array.isArray(resp?.data?.results) ? resp.data.results : [];
        setBankSuggestions(rows);
        setBankSuggestionsOpen(rows.length > 0);
      } catch (_) {
        setBankSuggestions([]);
        setBankSuggestionsOpen(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [formData?.bank_name, isViewMode]);

  const validateForm = () => {
    const errors = {};

    const accountNumberDigits = (formData?.account_number || '').replace(/\D/g, '');
    if (!accountNumberDigits) {
      errors.account_number = 'Обязательное поле';
    } else if (accountNumberDigits.length !== 20) {
      errors.account_number = 'Номер счета должен содержать 20 цифр';
    }

    if (!formData?.bank_name || !formData.bank_name.trim()) {
      errors.bank_name = 'Обязательное поле';
    }

    const bikDigits = (formData?.bik || '').replace(/\D/g, '');
    if (!bikDigits) {
      errors.bik = 'Обязательное поле';
    } else if (bikDigits.length !== 9) {
      errors.bik = 'БИК должен содержать 9 цифр';
    }

    const corrDigits = (formData?.corr_account || '').replace(/\D/g, '');
    if (corrDigits && corrDigits.length !== 20) {
      errors.corr_account = 'Корр. счет должен содержать 20 цифр';
    }

    if (!formData?.owner_type) {
      errors.owner_type = 'Укажите тип владельца';
    }
    if (formData?.owner_type && !formData?.owner_id) {
      errors.owner_id = 'Выберите владельца';
    }

    return errors;
  };

  const ownerOptions = useMemo(() => {
    if (formData?.owner_type === 'debtor') return debtors || [];
    if (formData?.owner_type === 'organizer') return organizers || [];
    if (formData?.owner_type === 'platform') return etps || [];
    return [];
  }, [formData?.owner_type, debtors, organizers, etps]);

  const ownerLabel = formData?.owner_type === 'debtor'
    ? 'Должник'
    : formData?.owner_type === 'organizer'
      ? 'Организатор / Управляющий'
      : 'ЭТП';

  const currentOwnerType = String(formData?.owner_type || '').trim().toLowerCase();
  const forcedOwnerId = forcedOwnerIdsByType?.[currentOwnerType] ?? '';
  const isOwnerLockedByContext = !!forcedOwnerId;
  const canCreateOwner = !isViewMode && !lockOwnerType && !lockOwnerId && !isOwnerLockedByContext && !!onCreateOwner && formData?.owner_type && formData?.owner_type !== 'platform';
  const canEditOwner = !isViewMode && !lockOwnerType && !lockOwnerId && !isOwnerLockedByContext && !!onEditOwner && formData?.owner_type && formData?.owner_type !== 'platform' && formData?.owner_id;

  useEffect(() => {
    if (!formData?.owner_type) return;
    if (!forcedOwnerId) return;
    if (String(formData?.owner_id || '') === String(forcedOwnerId)) return;
    setFormData((prev) => ({ ...(prev || {}), owner_id: forcedOwnerId }));
  }, [formData?.owner_type, formData?.owner_id, forcedOwnerId, setFormData]);

  const runSave = async (shouldExit = false) => {
    if (isViewMode || isSaving) return false;
    const errors = validateForm();
    setValidationErrors(errors);
    if (Object.keys(errors).length > 0) {
      return false;
    }

    if (!onSave) return false;

    setIsSaving(true);
    try {
      const payload = { ...(formData || {}) };
      const result = await onSave(payload, { exit: shouldExit });

      if (result === false) {
        return false;
      }

      const snapshot = JSON.parse(JSON.stringify(payload));
      setInitialFormData(snapshot);
      setValidationErrors({});
      if (onFormChange) {
        onFormChange(false);
      }
      return result;
    } catch (error) {
      console.error('Ошибка при сохранении счета:', error);
      throw error;
    } finally {
      setIsSaving(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    runSave(false).catch(() => {});
  };

  const renderField = ({ label, name, type = 'text', required = false, options = [], placeholder, maxLength, disabled = false }) => {
    const error = validationErrors[name];
    const fieldStyle = {
      marginBottom: '20px'
    };

    const labelStyle = {
      display: 'block',
      marginBottom: '8px',
      fontWeight: '500',
      color: '#2c3e50',
      fontSize: '14px'
    };

    const inputStyle = {
      width: '100%',
      padding: '12px',
      border: error ? '2px solid #e74c3c' : '1px solid #ddd',
      borderRadius: '6px',
      fontSize: '14px',
      fontFamily: 'inherit',
      backgroundColor: isViewMode || disabled ? '#f1f3f5' : 'white',
      color: isViewMode || disabled ? '#6c757d' : '#2c3e50',
      outline: 'none',
      transition: 'border-color 0.2s ease',
      cursor: isViewMode || disabled ? 'not-allowed' : 'text',
      pointerEvents: isViewMode || disabled ? 'none' : 'auto',
      opacity: isViewMode || disabled ? 0.8 : 1
    };

    const errorStyle = {
      color: '#e74c3c',
      fontSize: '12px',
      marginTop: '4px',
      display: 'block'
    };

    return (
      <div style={fieldStyle}>
        <label style={labelStyle}>
          {label}
          {required && <span style={{ color: '#e74c3c' }}>*</span>}
        </label>
        
        {type === 'select' ? (
          <select
            value={formData[name] || ''}
            onChange={(e) => updateFormData(name, e.target.value)}
            style={inputStyle}
            required={required}
            disabled={isViewMode}
          >
            {options.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        ) : (
          <input
            type={type}
            value={formData[name] || ''}
            onChange={(e) => updateFormData(name, e.target.value)}
            style={inputStyle}
            placeholder={placeholder}
            required={required}
            disabled={isViewMode}
            maxLength={maxLength}
          />
        )}
        
        {error && <span style={errorStyle}>{error}</span>}
      </div>
    );
  };

  return (
    <form onSubmit={handleSubmit}>
      {!!account && (
        <div style={{ marginBottom: 14 }}>
          <OwnerBadge email={account.owner_email} />
        </div>
      )}
      {renderField({
        label: 'Номер счета',
        name: 'account_number',
        required: true,
        placeholder: '40702810123456789012',
        maxLength: 20
      })}

      <div style={{ position: 'relative' }}>
        {renderField({
          label: 'Название банка',
          name: 'bank_name',
          required: true,
          placeholder: 'Начните вводить название банка'
        })}
        {bankSuggestionsOpen && !isViewMode && (
          <div
            style={{
              position: 'absolute',
              top: '72px',
              left: 0,
              right: 0,
              zIndex: 10,
              maxHeight: '220px',
              overflowY: 'auto',
              background: '#fff',
              border: '1px solid #d8dee6',
              borderRadius: '8px',
              boxShadow: '0 8px 20px rgba(0,0,0,0.08)',
            }}
          >
            {bankSuggestions.map((item, idx) => (
              <button
                key={`${item.bik || ''}-${idx}`}
                type="button"
                onClick={() => applyBankData(item)}
                style={{
                  width: '100%',
                  border: 'none',
                  background: '#fff',
                  textAlign: 'left',
                  padding: '10px 12px',
                  cursor: 'pointer',
                  borderBottom: idx === bankSuggestions.length - 1 ? 'none' : '1px solid #eef2f6',
                }}
              >
                <div style={{ fontSize: '14px', color: '#2c3e50' }}>{item.bank_name}</div>
                <div style={{ fontSize: '12px', color: '#6c757d' }}>
                  БИК: {item.bik || '—'} · Корр. счет: {item.corr_account || '—'}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
        {renderField({
          label: 'БИК',
          name: 'bik',
          required: true,
          placeholder: '044525225',
          maxLength: 9
        })}

        {renderField({
          label: 'Корр. счет',
          name: 'corr_account',
          required: true,
          placeholder: '30101810400000000225',
          maxLength: 20
        })}
      </div>

      {renderField({
        label: 'Назначение счета',
        name: 'purpose',
        type: 'select',
        required: true,
        disabled: lockPurpose,
        options: [
          { value: 'deposit', label: 'Задаток' },
          { value: 'payment', label: 'Оплата по договору' },
          { value: 'other', label: 'Прочее' }
        ]
      })}

      {renderField({
        label: 'Тип владельца',
        name: 'owner_type',
        type: 'select',
        required: true,
        disabled: lockOwnerType,
        options: [
          { value: 'debtor', label: 'Должник' },
          { value: 'organizer', label: 'Организатор' },
          { value: 'platform', label: 'Площадка' }
        ]
      })}

      {formData?.owner_type && (
        <div style={{ marginBottom: '20px' }}>
          <label style={{
            display: 'block',
            marginBottom: '8px',
            fontWeight: '500',
            color: '#2c3e50',
            fontSize: '14px'
          }}>
            {ownerLabel}
          </label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <SearchableSelect
              value={formData.owner_id}
              onChange={(value) => updateFormData('owner_id', value)}
              options={ownerOptions}
              placeholder={formData.owner_type === 'platform' ? 'Выберите ЭТП' : 'Выберите владельца'}
              searchPlaceholder="Поиск..."
              displayKey="display_name"
              valueKey="id"
              disabled={isViewMode || lockOwnerId || isOwnerLockedByContext}
              formatOption={(item) => {
                if (!item) return '';
                if (formData.owner_type === 'platform') return formatEtpDisplay(item);
                if (formData.owner_type === 'organizer') {
                  const label = item.display_name || item.name || '';
                  if (item.type === 'arbitrage_manager') return `${label} (АУ)`;
                  if (item.type === 'specialized_org') return `${label} (ОТ)`;
                }
                return item.display_name || item.name || '';
              }}
              formatSelected={(item) => {
                if (!item) return '';
                if (formData.owner_type === 'platform') return formatEtpSelected(item);
                if (formData.owner_type === 'organizer') {
                  const label = item.display_name || item.name || '';
                  if (item.type === 'arbitrage_manager') return `${label} (АУ)`;
                  if (item.type === 'specialized_org') return `${label} (ОТ)`;
                }
                return item.display_name || item.name || '';
              }}
              required
              showValidation={!!validationErrors.owner_id}
              onAddNew={canCreateOwner ? () => onCreateOwner(formData.owner_type) : undefined}
              addNewLabel={formData.owner_type === 'organizer' ? 'Добавить организатора (ОТ/АУ)' : 'Добавить должника'}
            />
            {canEditOwner && (
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm edit-btn"
                title="Редактировать"
                onClick={() => onEditOwner && onEditOwner(formData.owner_type, formData.owner_id)}
              >
                ✏️
              </button>
            )}
          </div>
          {isOwnerLockedByContext && (
            <span style={{ color: '#6c757d', fontSize: '12px', marginTop: '4px', display: 'block' }}>
              Значение подтягивается из карточки торгов.
            </span>
          )}
          {validationErrors.owner_id && (
            <span style={{ color: '#e74c3c', fontSize: '12px', marginTop: '4px', display: 'block' }}>
              {validationErrors.owner_id}
            </span>
          )}
        </div>
      )}

      {!isViewMode && (
        <div style={{ 
          display: 'flex', 
          gap: '12px', 
          justifyContent: 'flex-end',
          marginTop: '32px',
          paddingTop: '24px',
          borderTop: '1px solid #e1e5e9'
        }}>
          <button
            type="button"
            onClick={onCancel}
            style={{
              padding: '12px 24px',
              backgroundColor: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              transition: 'all 0.2s ease'
            }}
            disabled={isSaving}
          >
            Отмена
          </button>
          <button
            type="button"
            onClick={() => runSave(false).catch(() => {})}
            style={{
              padding: '12px 24px',
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              transition: 'all 0.2s ease'
            }}
            disabled={isSaving}
          >
            {isSaving ? 'Сохранение...' : 'Сохранить'}
          </button>
          <button
            type="button"
            onClick={() => runSave(true).catch(() => {})}
            style={{
              padding: '12px 24px',
              backgroundColor: '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              transition: 'all 0.2s ease'
            }}
            disabled={isSaving}
          >
            {isSaving ? 'Сохранение...' : 'Сохранить и выйти'}
          </button>
        </div>
      )}
      
      {isViewMode && (
        <div style={{ 
          display: 'flex', 
          gap: '12px', 
          justifyContent: 'flex-end',
          marginTop: '32px',
          paddingTop: '24px',
          borderTop: '1px solid #e1e5e9'
        }}>
          <button
            type="button"
            onClick={onCancel}
            style={{
              padding: '12px 24px',
              backgroundColor: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              transition: 'all 0.2s ease'
            }}
          >
            Закрыть
          </button>
        </div>
      )}
    </form>
  );
};

export default AccountForm;
